// src/components/TextField.js
import React, { useEffect, useRef, useState } from "react";
import InputMask from "react-input-mask";
import Select from "react-select";
// If a client later wants the Signotec hardware pad back, uncomment:
// import SignotecPad from "./SignotecPad";

/* ------------------ helpers ------------------ */
const toSafeId = (s) =>
  String(s ?? "field")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9\-_:./]/g, "");

function parseImageSize(s) {
  if (!s) return { w: 256, h: 256 };
  const cleaned = String(s).toLowerCase().replace(/[×*]/g, "x").replace(/\s+/g, "");
  const parts = cleaned.split("x").filter(Boolean);
  if (parts.length === 1) {
    const n = parseInt(parts[0], 10);
    if (Number.isFinite(n) && n > 0) return { w: n, h: n };
  } else if (parts.length >= 2) {
    const w = parseInt(parts[0], 10);
    const h = parseInt(parts[1], 10);
    if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) return { w, h };
  }
  return { w: 256, h: 256 };
}

async function fileToSizedDataUrl(file, w, h, type = "image/png", quality = 0.92) {
  const img = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result);
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = url;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context not available");

  // cover-fit
  const scale = Math.max(w / img.width, h / img.height);
  const newW = img.width * scale;
  const newH = img.height * scale;
  const dx = (w - newW) / 2;
  const dy = (h - newH) / 2;

  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(img, dx, dy, newW, newH);

  return canvas.toDataURL(type, quality);
}

/* ------------------ subcomponents ------------------ */

// Signature capture: draw on canvas OR upload a photo; emits base64 PNG via onChange
function SignatureField({
  id,
  label,
  required,
  hidden,
  disabled,
  imageSize,
  value,
  onChange,
}) {
  const { w, h } = imageSize ? parseImageSize(imageSize) : { w: 360, h: 160 };
  const [mode, setMode] = useState("draw"); // "draw" | "upload"
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPtRef = useRef({ x: 0, y: 0 });

  // prepare canvas
  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // white bg + stroke defaults
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 2;
  }, [w, h, mode]);

  const getPt = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const isTouch = e.type.startsWith("touch");
    const touch = isTouch ? (e.touches?.[0] || e.changedTouches?.[0]) : null;
    const clientX = isTouch ? touch.clientX : e.clientX;
    const clientY = isTouch ? touch.clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    if (disabled || mode !== "draw") return;
    drawingRef.current = true;
    lastPtRef.current = getPt(e);
    e.preventDefault();
  };
  const move = (e) => {
    if (!drawingRef.current || disabled || mode !== "draw") return;
    const ctx = canvasRef.current.getContext("2d");
    const pt = getPt(e);
    ctx.beginPath();
    ctx.moveTo(lastPtRef.current.x, lastPtRef.current.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    lastPtRef.current = pt;
    e.preventDefault();
  };
  const end = (e) => {
    drawingRef.current = false;
    e.preventDefault();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // repaint white bg at logical size
    const tmp = document.createElement("canvas");
    tmp.width = w; tmp.height = h;
    const tctx = tmp.getContext("2d");
    tctx.fillStyle = "#ffffff";
    tctx.fillRect(0, 0, w, h);
    // scale back into device canvas
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.drawImage(tmp, 0, 0, w, h, 0, 0, w, h);
  };

  const applyDrawing = () => {
    const src = canvasRef.current;
    if (!src) return;
    // normalize to logical w x h
    const tmp = document.createElement("canvas");
    tmp.width = w; tmp.height = h;
    const tctx = tmp.getContext("2d");
    // draw device canvas to logical size
    tctx.drawImage(src, 0, 0, src.width, src.height, 0, 0, w, h);
    const dataUrl = tmp.toDataURL("image/png", 0.92);
    onChange?.({ target: { id, type: "text", value: dataUrl } });
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Please choose an image file.");
      return;
    }
    try {
      const dataUrl = await fileToSizedDataUrl(file, w, h, "image/png", 0.92);
      onChange?.({ target: { id, type: "text", value: dataUrl } });
    } catch {
      alert("Could not process image. Please try a different file.");
    }
  };

  const clearValue = () => onChange?.({ target: { id, type: "text", value: "" } });

  return (
    <div className={`flex flex-col gap-2 items-start w-full ${hidden ? "hidden" : ""}`}>
      <label htmlFor={id} className="form-label">
        <span className={`${required ? 'after:content-["*"] after:text-red-600/70' : ""}`}>{label}</span>
        <span className="ml-2 text-xs text-gray-500">({w}×{h}px)</span>
      </label>

      <div className="inline-flex rounded-md border overflow-hidden">
        <button
          type="button"
          className={`px-3 py-1.5 text-sm ${mode === "draw" ? "bg-[#0E86D4] text-white" : "bg-white text-gray-700"}`}
          onClick={() => setMode("draw")}
          disabled={disabled}
        >
          Draw
        </button>
        <button
          type="button"
          className={`px-3 py-1.5 text-sm border-l ${mode === "upload" ? "bg-[#0E86D4] text-white" : "bg-white text-gray-700"}`}
          onClick={() => setMode("upload")}
          disabled={disabled}
        >
          Upload
        </button>
      </div>

      {mode === "draw" && (
        <div className="flex flex-col gap-2">
          <div className="border rounded bg-white" style={{ width: w, height: h, touchAction: "none" }}>
            <canvas
              ref={canvasRef}
              onMouseDown={start}
              onMouseMove={move}
              onMouseUp={end}
              onMouseLeave={end}
              onTouchStart={start}
              onTouchMove={move}
              onTouchEnd={end}
              className="block"
              style={{ width: `${w}px`, height: `${h}px` }}
            />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={clearCanvas} disabled={disabled} className="px-3 py-1.5 text-sm bg-gray-100 border rounded hover:bg-gray-200">
              Clear Canvas
            </button>
            <button type="button" onClick={applyDrawing} disabled={disabled} className="px-3 py-1.5 text-sm bg-[#0E86D4] text-white rounded hover:bg-[#0d78bd]">
              Use Drawing
            </button>
          </div>
        </div>
      )}

      {mode === "upload" && (
        <div className="flex items-center gap-3">
          <input
            id={id}
            type="file"
            accept="image/*"
            capture="environment"
            disabled={disabled}
            onChange={handleFile}
            className="block text-sm text-gray-700 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-gray-200 file:text-gray-800 hover:file:bg-gray-300 disabled:opacity-50"
          />
        </div>
      )}

      <div className="border border-dashed border-gray-300 rounded p-2 bg-white" style={{ width: w + 16, maxWidth: "100%" }}>
        <div className="mx-auto bg-gray-50 flex items-center justify-center overflow-hidden" style={{ width: w, height: h }}>
          {value ? (
            <img src={value} alt={label || "signature"} style={{ width: w, height: h, objectFit: "cover" }} />
          ) : (
            <span className="text-xs text-gray-400">No signature (will be {w}×{h})</span>
          )}
        </div>
      </div>

      {value && (
        <button
          type="button"
          onClick={clearValue}
          disabled={disabled}
          className="px-3 py-1.5 text-sm bg-gray-100 border rounded hover:bg-gray-200"
        >
          Clear Value
        </button>
      )}

      {/* 🔌 To use a real Signotec hardware pad later, you can replace this component:
      
      <SignotecPad
        id={id}
        className="form-input"
        onComplete={(dataUrl) => onChange?.({ target: { id, type: "text", value: dataUrl } })}
        // ...other props (device init, etc.)
      />
      
      */}
    </div>
  );
}

// Generic image uploader (resizes to imageSize) that emits base64 PNG
function ImageField({ id, label, required, hidden, disabled, imageSize, value, onChange }) {
  const { w, h } = parseImageSize(imageSize);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Please choose an image file.");
      return;
    }
    try {
      const dataUrl = await fileToSizedDataUrl(file, w, h, "image/png", 0.92);
      onChange?.({ target: { id, type: "text", value: dataUrl } });
    } catch {
      alert("Could not process image. Please try a different file.");
    }
  };

  const handleClear = () => onChange?.({ target: { id, type: "text", value: "" } });

  return (
    <div className={`flex flex-col gap-2 items-start w-full ${hidden ? "hidden" : ""}`}>
      <label htmlFor={id} className="form-label">
        <span className={`${required ? 'after:content-["*"] after:text-red-600/70' : ""}`}>{label}</span>
        <span className="ml-2 text-xs text-gray-500">({w}×{h}px)</span>
      </label>

      <div className="flex items-center gap-3">
        <input
          id={id}
          type="file"
          accept="image/*"
          capture="environment"
          disabled={disabled}
          onChange={handleFile}
          className="block text-sm text-gray-700 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-gray-200 file:text-gray-800 hover:file:bg-gray-300 disabled:opacity-50"
        />
        {value ? (
          <button type="button" onClick={handleClear} className="px-3 py-1.5 text-sm bg-gray-100 border border-gray-300 rounded hover:bg-gray-200">
            Clear
          </button>
        ) : null}
      </div>

      <div className="border border-dashed border-gray-300 rounded p-2 bg-white" style={{ width: w + 16, maxWidth: "100%" }}>
        <div className="mx-auto bg-gray-50 flex items-center justify-center overflow-hidden" style={{ width: w, height: h }}>
          {value ? (
            <img src={value} alt={label || "uploaded"} style={{ width: w, height: h, objectFit: "cover" }} />
          ) : (
            <span className="text-xs text-gray-400">No image (will be {w}×{h})</span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------ main component ------------------ */

// src/components/TextField.js
const TextField = ({
  label,
  onBlur,
  defaultValue,
  options,
  // ❗ take custom (non-DOM) props explicitly so they DON'T go into ...domProps
  isImage,
  imageSize,
  uploadsBaseUrl,
  ...rest
}) => {
  // split rest into known + the DOM-safe remainder
  const {
    required,
    type,
    hidden,
    id: idProp,
    value,
    onChange,
    disabled,
    // anything else in rest should be safe to pass to DOM:
    ...domProps
  } = rest;

  const id = idProp || toSafeId(label);
  const fieldOptions = Array.isArray(options)
    ? options.map((o) => (typeof o === "string" ? o : o?.fieldValue))
    : [];

  // --- Signature ---
  if (type === "signature") {
    return (
      <SignatureField
        id={id}
        label={label}
        required={required}
        hidden={hidden}
        disabled={disabled}
        imageSize={imageSize}
        value={value}
        onChange={onChange}
      />
    );
  }

  // --- Image uploader ---
  if (isImage) {
    return (
      <ImageField
        id={id}
        label={label}
        required={required}
        hidden={hidden}
        disabled={disabled}
        imageSize={imageSize}
        value={value}
        onChange={onChange}
      />
    );
  }

  // --- Checkbox ---
  if (type === "checkbox") {
    return (
      <div className={`flex ${hidden ? "hidden" : ""}`}>
       
        <input  type="checkbox" id={id}  onChange={onChange}  {...domProps} />
        <label className={`${required ? 'after:content-["*"] after:text-red-600/70' : ""} pl-4`} htmlFor={id}>
          {label}
        </label>
      </div>
    );
  }

  // --- Radio ---
  if (type === "radio") {
    return (
      <div className={`flex flex-col gap-2 ${hidden ? "hidden" : ""}`}>
        <span>{label}</span>
        <div className="flex flex-wrap gap-4">
          {fieldOptions.map((option, index) => (
            <label key={index} className="flex items-center gap-2">
              <input type="radio"  onChange={onChange} name={id} value={option} className="w-5 h-5" autoComplete="off" {...domProps} />
              <span>{option}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  // --- Account number ---
  if (type === "accountnumber") {
    return (
      <div className={`flex flex-col items-start w-full space-y-1 ${hidden ? "hidden" : ""}`}>
        <label htmlFor={id} className="form-label">
          <span className={required ? 'after:content-["*"] after:text-red-600/70' : ""}>{label}</span>
        </label>
        <InputMask  onChange={onChange} mask="999999999999/9" id={id} className="form-input" maskChar="" onBlur={onBlur} {...domProps} />
      </div>
    );
  }

  // --- Textarea ---
  if (type === "textarea") {
    return (
      <div className={`flex flex-col space-y-1 items-start w-full ${hidden ? "hidden" : ""}`}>
        <label htmlFor={id} className="form-label">
          <span className={required ? 'after:content-["*"] after:text-red-600/70' : ""}>{label}</span>
        </label>
        <textarea  onChange={onChange} id={id} rows="4" cols="50" className="form-txtarea" autoComplete="off" onBlur={onBlur} {...domProps} />
      </div>
    );
  }

  // --- Ghana card ---
  if (type === "ghanacard") {
    return (
      <div className={`flex flex-col items-start w-full ${hidden ? "hidden" : ""}`}>
        <label htmlFor={id} className="form-label">
          <span className={required ? 'after:content-["*"] after:text-red-600/70' : ""}>{label}</span>
        </label>
        <InputMask  onChange={onChange} mask="aaa-999999999-*" id={id} className="form-input" maskChar="" onBlur={onBlur} {...domProps} />
      </div>
    );
  }

  // --- Dropdown ---
  if (type === "dropdown") {
    return (
      <div className={`flex flex-col items-start w-full space-y-1 ${hidden ? "hidden" : ""}`}>
        <label htmlFor={id} className="form-label">
          <span className={required ? 'after:content-["*"] after:text-red-600/70' : ""}>{label}</span>
        </label>
        <select name={id} id={id}  onChange={onChange} className="form-input" {...domProps}>
          <option key={-1} value="" className="text-slate-300">select an option</option>
          {fieldOptions.map((option, index) => (
            <option key={index} value={option}>{option}</option>
          ))}
        </select>
      </div>
    );
  }

  // --- Number ---
  if (type === "number") {
    return (
      <div className={`flex flex-col space-y-1 items-start w-full ${hidden ? "hidden" : ""}`}>
        <label htmlFor={id} className="form-label">
          <span className={required ? 'after:content-["*"] after:text-red-600/70' : ""}>{label}</span>
        </label>
        <input  onChange={onChange} id={id} type="number" className="form-input" autoComplete="off" step={0.01} min={1} onBlur={onBlur} {...domProps} />
      </div>
    );
  }

  // --- Ghana tel ---
  if (type === "ghanatel") {
    return (
      <div className={`flex flex-col items-start w-full ${hidden ? "hidden" : ""}`}>
        <label htmlFor={id} className="form-label">
          <span className={required ? 'after:content-["*"] after:text-red-600/70' : ""}>{label}</span>
        </label>
        <InputMask  onChange={onChange} mask="+233-999-999-999" id={id} className="form-input" maskChar="" onBlur={onBlur} {...domProps} />
      </div>
    );
  }

  // --- Multiple ---
  if (type === "multiple") {
    const opts = fieldOptions.map((o) => ({ value: o, label: o }));
    return (
      <div className={`flex flex-col items-start w-full ${hidden ? "hidden" : ""}`}>
        <label htmlFor={id} className="form-label">
          <span className={required ? 'after:content-["*"] after:text-red-600/70' : ""}>{label}</span>
        </label>
        <Select  onChange={onChange} isMulti options={opts} className="form-input" {...domProps} />
      </div>
    );
  }

  if(type === "calculated"){
    return (
    <div className={`flex flex-col space-y-1 items-start w-full ${hidden ? "hidden" : ""}`}>
      <label htmlFor={id} className="form-label">
        <span className={required ? 'after:content-["*"] after:text-red-600/70' : ""}>{label}</span>
      </label>
      <input value={value}  onChange={onChange} id={id} className="form-input" autoComplete="off" onBlur={onBlur} {...domProps} />
    </div>
  );
  }

 

  // --- Default ---
  return (
    <div className={`flex flex-col space-y-1 items-start w-full ${hidden ? "hidden" : ""}`}>
      <label htmlFor={id} className="form-label">
        <span className={required ? 'after:content-["*"] after:text-red-600/70' : ""}>{label}</span>
      </label>
      <input  onChange={onChange} id={id} className="form-input" autoComplete="off" onBlur={onBlur} {...domProps} />
    </div>
  );
};


export default TextField;
